import React, { useMemo, useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import {
  Package,
  CheckCircle2,
  AlertTriangle,
  XCircle,
  Plus,
  Search,
  ChevronDown,
  ChevronRight,
  ChevronLeft,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useToast } from '@/hooks/use-toast';

// ─── Types ────────────────────────────────────────────────────────────────────

interface SwItem {
  name: string;
  version?: string;
  vendor?: string;
  install_date?: string | null;
  size_kb?: number | null;
  matched_software_id?: string | null;
}

interface DiscoveredAsset {
  ativo_id: string;
  cmdb_ativos: {
    id: string;
    nome: string;
    software_instalado: SwItem[] | null;
  } | null;
}

interface CatalogSoftware {
  id: string;
  nome: string;
  versao: string | null;
  categoria: string | null;
  tipo_licenciamento: string;
  quantidade_contratada: number | null;
  status: string;
  fabricante_nome?: string;
}

/** Aggregated view of a unique installed software name across all discovered assets */
interface InstalledGroup {
  name: string;
  vendor: string;
  version: string;
  deviceCount: number;
  deviceNames: string[];
  catalogMatch: CatalogSoftware | null;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function normalize(s: string) {
  return s.toLowerCase().replace(/[^a-z0-9]/g, ' ').replace(/\s+/g, ' ').trim();
}

function matchToCatalog(name: string, catalog: CatalogSoftware[]): CatalogSoftware | null {
  const norm = normalize(name);
  if (!norm) return null;

  // Exact match first
  const exact = catalog.find(c => normalize(c.nome) === norm);
  if (exact) return exact;

  // Substring match: catalog name inside installed name or vice-versa
  // Prefer longer catalog name match (more specific)
  const matches = catalog.filter(c => {
    const cn = normalize(c.nome);
    return cn.length >= 3 && (norm.includes(cn) || cn.includes(norm));
  });
  if (matches.length === 0) return null;
  return matches.sort((a, b) => b.nome.length - a.nome.length)[0];
}

// ─── Hooks ───────────────────────────────────────────────────────────────────

function useReconciliationData(clientId: string | null) {
  return useQuery({
    queryKey: ['reconciliation', clientId],
    queryFn: async () => {
      if (!clientId) return { assets: [] as DiscoveredAsset[], catalog: [] as CatalogSoftware[] };

      const [assetsRes, catalogRes] = await Promise.all([
        supabase
          .from('discovery_device_map')
          .select('ativo_id, cmdb_ativos(id, nome, software_instalado)')
          .eq('client_id', clientId),
        supabase
          .from('cmdb_softwares')
          .select('id, nome, versao, categoria, tipo_licenciamento, quantidade_contratada, status, fabricantes(nome)')
          .eq('client_id', clientId)
          .eq('status', 'ativo'),
      ]);

      if (assetsRes.error) throw assetsRes.error;
      if (catalogRes.error) throw catalogRes.error;

      const catalog: CatalogSoftware[] = (catalogRes.data ?? []).map((s: any) => ({
        ...s,
        fabricante_nome: s.fabricantes?.nome ?? null,
      }));

      return {
        assets: (assetsRes.data ?? []) as unknown as DiscoveredAsset[],
        catalog,
      };
    },
    enabled: !!clientId,
    staleTime: 60_000,
  });
}

// ─── Create catalog dialog ────────────────────────────────────────────────────

interface CreateFromDiscoveryDialogProps {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  clientId: string;
  initialName: string;
  initialVendor: string;
  initialVersion: string;
  onCreated: () => void;
}

function CreateFromDiscoveryDialog({
  open,
  onOpenChange,
  clientId,
  initialName,
  initialVendor,
  initialVersion,
  onCreated,
}: CreateFromDiscoveryDialogProps) {
  const { toast } = useToast();
  const [nome, setNome] = useState(initialName);
  const [versao, setVersao] = useState(initialVersion);
  const [categoria, setCategoria] = useState('');
  const [tipo, setTipo] = useState('perpetua');
  const [qtd, setQtd] = useState('1');

  React.useEffect(() => {
    if (open) {
      setNome(initialName);
      setVersao(initialVersion);
      setCategoria('');
      setTipo('perpetua');
      setQtd('1');
    }
  }, [open, initialName, initialVersion]);

  const create = useMutation({
    mutationFn: async () => {
      const { error } = await supabase.from('cmdb_softwares').insert({
        client_id: clientId,
        nome,
        versao: versao || null,
        categoria: categoria || null,
        tipo_licenciamento: tipo,
        quantidade_contratada: parseInt(qtd) || 1,
        status: 'ativo',
      });
      if (error) throw error;
    },
    onSuccess: () => {
      toast({ title: `"${nome}" adicionado ao catálogo.` });
      onCreated();
      onOpenChange(false);
    },
    onError: (e: Error) => toast({ title: e.message, variant: 'destructive' }),
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Adicionar ao Catálogo de Software</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1">
            <Label>Nome *</Label>
            <Input value={nome} onChange={e => setNome(e.target.value)} />
          </div>
          <div className="space-y-1">
            <Label>Versão</Label>
            <Input value={versao} onChange={e => setVersao(e.target.value)} placeholder="Ex: 1.0.0" />
          </div>
          <div className="space-y-1">
            <Label>Categoria</Label>
            <Input value={categoria} onChange={e => setCategoria(e.target.value)} placeholder="Ex: Segurança, Produtividade" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label>Tipo de licença</Label>
              <select
                className="w-full border rounded-md px-3 py-2 text-sm"
                value={tipo}
                onChange={e => setTipo(e.target.value)}
              >
                <option value="perpetua">Perpétua</option>
                <option value="assinatura">Assinatura</option>
                <option value="por_usuario">Por usuário</option>
                <option value="por_dispositivo">Por dispositivo</option>
                <option value="volume">Volume</option>
              </select>
            </div>
            <div className="space-y-1">
              <Label>Qtd. contratada</Label>
              <Input
                type="number"
                min={1}
                value={qtd}
                onChange={e => setQtd(e.target.value)}
              />
            </div>
          </div>
          {initialVendor && (
            <p className="text-xs text-gray-400">Fabricante detectado: {initialVendor}</p>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button onClick={() => create.mutate()} disabled={!nome.trim() || create.isPending}>
            {create.isPending ? 'Salvando...' : 'Adicionar ao catálogo'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Pagination ───────────────────────────────────────────────────────────────

const PAGE_SIZE = 12;

function Paginator({
  total,
  page,
  onPage,
}: {
  total: number;
  page: number;
  onPage: (p: number) => void;
}) {
  const totalPages = Math.ceil(total / PAGE_SIZE);
  if (totalPages <= 1) return null;

  return (
    <div className="flex items-center justify-between px-4 py-2 border-t text-sm text-gray-500">
      <span>
        {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, total)} de {total}
      </span>
      <div className="flex items-center gap-1">
        <Button
          variant="ghost"
          size="sm"
          className="h-7 w-7 p-0"
          disabled={page === 1}
          onClick={() => onPage(page - 1)}
        >
          <ChevronLeft className="h-4 w-4" />
        </Button>
        {Array.from({ length: totalPages }, (_, i) => i + 1)
          .filter(p => p === 1 || p === totalPages || Math.abs(p - page) <= 1)
          .reduce<(number | '...')[]>((acc, p, idx, arr) => {
            if (idx > 0 && (p as number) - (arr[idx - 1] as number) > 1) acc.push('...');
            acc.push(p);
            return acc;
          }, [])
          .map((p, i) =>
            p === '...' ? (
              <span key={`ellipsis-${i}`} className="px-1">…</span>
            ) : (
              <Button
                key={p}
                variant={p === page ? 'default' : 'ghost'}
                size="sm"
                className="h-7 w-7 p-0"
                onClick={() => onPage(p as number)}
              >
                {p}
              </Button>
            )
          )}
        <Button
          variant="ghost"
          size="sm"
          className="h-7 w-7 p-0"
          disabled={page === totalPages}
          onClick={() => onPage(page + 1)}
        >
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

interface SoftwareReconciliationProps {
  clientId: string;
}

export const SoftwareReconciliation: React.FC<SoftwareReconciliationProps> = ({ clientId }) => {
  const queryClient = useQueryClient();
  const { data, isLoading, error } = useReconciliationData(clientId);

  const [searchTerm, setSearchTerm] = useState('');
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [pageUnmatched, setPageUnmatched] = useState(1);
  const [pageCatalog, setPageCatalog] = useState(1);
  const [createDialog, setCreateDialog] = useState<{
    open: boolean;
    name: string;
    vendor: string;
    version: string;
  }>({ open: false, name: '', vendor: '', version: '' });

  // ── Build reconciliation ──────────────────────────────────────────────────

  const { installedGroups, catalogItems } = useMemo(() => {
    if (!data) return { installedGroups: [], catalogItems: [] };
    const { assets, catalog } = data;

    // Aggregate installed software across all assets
    const groupMap = new Map<string, InstalledGroup>();

    for (const asset of assets) {
      const items = asset.cmdb_ativos?.software_instalado ?? [];
      const hostname = asset.cmdb_ativos?.nome ?? '?';

      for (const sw of items) {
        if (!sw.name?.trim()) continue;
        const key = sw.name.toLowerCase().trim();

        if (!groupMap.has(key)) {
          groupMap.set(key, {
            name: sw.name,
            vendor: sw.vendor ?? '',
            version: sw.version ?? '',
            deviceCount: 0,
            deviceNames: [],
            catalogMatch: matchToCatalog(sw.name, catalog),
          });
        }

        const g = groupMap.get(key)!;
        g.deviceCount += 1;
        if (!g.deviceNames.includes(hostname)) g.deviceNames.push(hostname);
      }
    }

    // Catalog items with installation coverage
    const assetSwNames: Map<string, Set<string>> = new Map();
    for (const asset of assets) {
      const names = new Set(
        (asset.cmdb_ativos?.software_instalado ?? [])
          .map(sw => sw.name?.toLowerCase().trim())
          .filter(Boolean) as string[]
      );
      assetSwNames.set(asset.cmdb_ativos?.nome ?? asset.ativo_id, names);
    }

    const catalogItems = catalog.map(cat => {
      const catNorm = normalize(cat.nome);
      const installedOn: string[] = [];

      for (const [hostname, names] of assetSwNames) {
        const found = [...names].some(n => {
          const nn = normalize(n);
          return nn.includes(catNorm) || catNorm.includes(nn);
        });
        if (found) installedOn.push(hostname);
      }

      return { ...cat, installedCount: installedOn.length, installedOn };
    });

    return {
      installedGroups: [...groupMap.values()].sort((a, b) => b.deviceCount - a.deviceCount),
      catalogItems,
    };
  }, [data]);

  const unmatched = installedGroups.filter(g => !g.catalogMatch);
  const matched   = installedGroups.filter(g =>  g.catalogMatch);

  const filteredUnmatched = useMemo(() => {
    if (!searchTerm) return unmatched;
    const t = searchTerm.toLowerCase();
    return unmatched.filter(g =>
      g.name.toLowerCase().includes(t) ||
      g.vendor.toLowerCase().includes(t)
    );
  }, [unmatched, searchTerm]);

  const filteredCatalog = useMemo(() => {
    if (!searchTerm) return catalogItems;
    const t = searchTerm.toLowerCase();
    return catalogItems.filter(c =>
      c.nome.toLowerCase().includes(t) ||
      (c.fabricante_nome ?? '').toLowerCase().includes(t)
    );
  }, [catalogItems, searchTerm]);

  // Reset pages when search or data changes
  useEffect(() => {
    setPageUnmatched(1);
    setPageCatalog(1);
    setExpandedRows(new Set());
  }, [searchTerm]);

  const toggleExpand = (key: string) => {
    setExpandedRows(prev => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  };

  // ── Render ────────────────────────────────────────────────────────────────

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16 text-gray-400">
        <Package className="h-5 w-5 mr-2 animate-pulse" />
        Carregando dados de reconciliação...
      </div>
    );
  }

  if (error) {
    return (
      <div className="py-8 text-center text-red-500">
        Erro ao carregar dados: {(error as Error).message}
      </div>
    );
  }

  const totalDevices = data?.assets.length ?? 0;
  const totalInstalled = installedGroups.length;

  return (
    <div className="space-y-5">
      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card>
          <CardContent className="pt-3 pb-3">
            <div className="flex items-center gap-2">
              <Package className="h-4 w-4 text-gray-400" />
              <div>
                <p className="text-xs text-gray-500">Software distintos</p>
                <p className="text-xl font-bold">{totalInstalled}</p>
              </div>
            </div>
            <p className="text-xs text-gray-400 mt-1">em {totalDevices} dispositivo(s)</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-3 pb-3">
            <div className="flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4 text-green-500" />
              <div>
                <p className="text-xs text-gray-500">Catalogados</p>
                <p className="text-xl font-bold text-green-600">{matched.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-3 pb-3">
            <div className="flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-yellow-500" />
              <div>
                <p className="text-xs text-gray-500">Sem catálogo</p>
                <p className="text-xl font-bold text-yellow-600">{unmatched.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-3 pb-3">
            <div className="flex items-center gap-2">
              <XCircle className="h-4 w-4 text-red-400" />
              <div>
                <p className="text-xs text-gray-500">Não instalados</p>
                <p className="text-xl font-bold text-red-500">
                  {catalogItems.filter(c => c.installedCount === 0).length}
                </p>
              </div>
            </div>
            <p className="text-xs text-gray-400 mt-1">do catálogo</p>
          </CardContent>
        </Card>
      </div>

      {/* Search */}
      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
        <Input
          placeholder="Buscar software..."
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          className="pl-9"
        />
      </div>

      {/* Section 1: Sem catálogo */}
      <div>
        <h3 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-1.5">
          <AlertTriangle className="h-4 w-4 text-yellow-500" />
          Instalados sem catálogo
          <Badge variant="secondary" className="ml-1">{filteredUnmatched.length}</Badge>
        </h3>

        {filteredUnmatched.length === 0 ? (
          <Card>
            <CardContent className="py-6 text-center text-sm text-gray-400">
              {searchTerm ? 'Nenhum resultado.' : 'Todos os softwares instalados estão catalogados.'}
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-6"></TableHead>
                    <TableHead>Nome</TableHead>
                    <TableHead>Fabricante</TableHead>
                    <TableHead>Versão</TableHead>
                    <TableHead>Dispositivos</TableHead>
                    <TableHead className="text-right">Ação</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredUnmatched
                    .slice((pageUnmatched - 1) * PAGE_SIZE, pageUnmatched * PAGE_SIZE)
                    .map(g => {
                      const key = g.name.toLowerCase();
                      const expanded = expandedRows.has(key);
                      return (
                        <React.Fragment key={key}>
                          <TableRow>
                            <TableCell className="py-2">
                              <button
                                type="button"
                                onClick={() => toggleExpand(key)}
                                className="text-gray-400 hover:text-gray-600"
                              >
                                {expanded
                                  ? <ChevronDown className="h-3.5 w-3.5" />
                                  : <ChevronRight className="h-3.5 w-3.5" />}
                              </button>
                            </TableCell>
                            <TableCell className="font-medium text-sm">{g.name}</TableCell>
                            <TableCell className="text-sm text-gray-500">{g.vendor || '—'}</TableCell>
                            <TableCell className="text-sm text-gray-500 font-mono">{g.version || '—'}</TableCell>
                            <TableCell>
                              <Badge variant="outline">{g.deviceCount}</Badge>
                            </TableCell>
                            <TableCell className="text-right">
                              <Button
                                size="sm"
                                variant="outline"
                                className="h-7 text-xs"
                                onClick={() =>
                                  setCreateDialog({
                                    open: true,
                                    name: g.name,
                                    vendor: g.vendor,
                                    version: g.version,
                                  })
                                }
                              >
                                <Plus className="h-3 w-3 mr-1" />
                                Catalogar
                              </Button>
                            </TableCell>
                          </TableRow>
                          {expanded && g.deviceNames.length > 0 && (
                            <TableRow>
                              <TableCell />
                              <TableCell colSpan={5} className="py-1 pb-2">
                                <p className="text-xs text-gray-400 mb-1">Dispositivos com este software:</p>
                                <div className="flex flex-wrap gap-1">
                                  {g.deviceNames.map(d => (
                                    <Badge key={d} variant="secondary" className="text-xs font-normal">
                                      {d}
                                    </Badge>
                                  ))}
                                </div>
                              </TableCell>
                            </TableRow>
                          )}
                        </React.Fragment>
                      );
                    })}
                </TableBody>
              </Table>
            </CardContent>
            <Paginator
              total={filteredUnmatched.length}
              page={pageUnmatched}
              onPage={setPageUnmatched}
            />
          </Card>
        )}
      </div>

      {/* Section 2: Catálogo × dispositivos */}
      <div>
        <h3 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-1.5">
          <Package className="h-4 w-4 text-blue-500" />
          Catálogo vs. dispositivos
          <Badge variant="secondary" className="ml-1">{filteredCatalog.length}</Badge>
        </h3>

        {filteredCatalog.length === 0 ? (
          <Card>
            <CardContent className="py-6 text-center text-sm text-gray-400">
              {searchTerm ? 'Nenhum resultado.' : 'Nenhum software catalogado para este cliente.'}
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-6"></TableHead>
                    <TableHead>Software</TableHead>
                    <TableHead>Tipo</TableHead>
                    <TableHead>Qtd. contratada</TableHead>
                    <TableHead>Instalado em</TableHead>
                    <TableHead>Cobertura</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredCatalog
                    .slice((pageCatalog - 1) * PAGE_SIZE, pageCatalog * PAGE_SIZE)
                    .map(c => {
                    const key = `cat-${c.id}`;
                    const expanded = expandedRows.has(key);
                    const coverage = totalDevices > 0
                      ? Math.round((c.installedCount / totalDevices) * 100)
                      : 0;
                    const overLicensed = c.quantidade_contratada !== null && c.installedCount > c.quantidade_contratada;

                    return (
                      <React.Fragment key={c.id}>
                        <TableRow>
                          <TableCell className="py-2">
                            {c.installedOn.length > 0 && (
                              <button
                                type="button"
                                onClick={() => toggleExpand(key)}
                                className="text-gray-400 hover:text-gray-600"
                              >
                                {expanded
                                  ? <ChevronDown className="h-3.5 w-3.5" />
                                  : <ChevronRight className="h-3.5 w-3.5" />}
                              </button>
                            )}
                          </TableCell>
                          <TableCell>
                            <div>
                              <p className="font-medium text-sm">{c.nome}</p>
                              {c.versao && <p className="text-xs text-gray-400">{c.versao}</p>}
                            </div>
                          </TableCell>
                          <TableCell className="text-sm text-gray-500 capitalize">
                            {c.tipo_licenciamento?.replace('_', ' ')}
                          </TableCell>
                          <TableCell className="text-sm">
                            {c.quantidade_contratada ?? '—'}
                          </TableCell>
                          <TableCell>
                            <span
                              className={`font-semibold text-sm ${
                                c.installedCount === 0
                                  ? 'text-gray-400'
                                  : overLicensed
                                  ? 'text-red-600'
                                  : 'text-green-600'
                              }`}
                            >
                              {c.installedCount}
                            </span>
                            {overLicensed && (
                              <Badge className="ml-2 text-xs bg-red-100 text-red-700 border-red-200">
                                acima do contratado
                              </Badge>
                            )}
                          </TableCell>
                          <TableCell>
                            {totalDevices > 0 ? (
                              <div className="flex items-center gap-2">
                                <div className="w-16 h-1.5 rounded-full bg-gray-100 overflow-hidden">
                                  <div
                                    className={`h-full rounded-full ${
                                      coverage >= 80 ? 'bg-green-500' : coverage >= 30 ? 'bg-yellow-500' : 'bg-gray-300'
                                    }`}
                                    style={{ width: `${coverage}%` }}
                                  />
                                </div>
                                <span className="text-xs text-gray-500">{coverage}%</span>
                              </div>
                            ) : '—'}
                          </TableCell>
                        </TableRow>
                        {expanded && c.installedOn.length > 0 && (
                          <TableRow>
                            <TableCell />
                            <TableCell colSpan={5} className="py-1 pb-2">
                              <p className="text-xs text-gray-400 mb-1">Instalado em:</p>
                              <div className="flex flex-wrap gap-1">
                                {c.installedOn.map(d => (
                                  <Badge key={d} variant="secondary" className="text-xs font-normal">
                                    {d}
                                  </Badge>
                                ))}
                              </div>
                            </TableCell>
                          </TableRow>
                        )}
                      </React.Fragment>
                    );
                  })}
                </TableBody>
              </Table>
            </CardContent>
            <Paginator total={filteredCatalog.length} page={pageCatalog} onPage={setPageCatalog} />
          </Card>
        )}
      </div>

      {/* Create dialog */}
      {createDialog.open && (
        <CreateFromDiscoveryDialog
          open={createDialog.open}
          onOpenChange={o => setCreateDialog(d => ({ ...d, open: o }))}
          clientId={clientId}
          initialName={createDialog.name}
          initialVendor={createDialog.vendor}
          initialVersion={createDialog.version}
          onCreated={() => queryClient.invalidateQueries({ queryKey: ['reconciliation', clientId] })}
        />
      )}
    </div>
  );
};
